/**
 * pages/countdown-create.tsx
 *
 * Form to create a new countdown event.
 * - Title input (required)
 * - Date input (required, min = today)
 * - Save button (disabled until title + date filled)
 * - Submits JSON via useCreateCountdown
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateCountdown } from '../hooks/use-countdowns'

const todayStr = (): string => {
  const d = new Date()
  const y = d.getFullYear()
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default function CountdownCreatePage() {
  const navigate = useNavigate()
  const createCountdown = useCreateCountdown()

  const [title, setTitle] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  const canSave = title.trim().length > 0 && targetDate.length > 0 && !createCountdown.isPending

  const handleSave = async () => {
    if (!canSave) return
    setError(null)

    try {
      await createCountdown.mutateAsync({ title: title.trim(), target_date: targetDate })
      navigate('/countdowns')
    } catch (err) {
      setError((err as Error).message ?? 'Failed to save countdown')
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            New Countdown
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-[34px] px-4 rounded-full bg-[#D4537E] text-white font-['DM_Sans'] text-[14px] font-medium shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-95 active:bg-[#993556] transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:shadow-none disabled:active:scale-100"
          >
            {createCountdown.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {/* Form */}
      <main className="flex-1 overflow-y-auto px-5 pt-5 pb-10 flex flex-col gap-4 animate-fade-in-up">
        {/* Error banner */}
        {error && (
          <div className="bg-[#FFF0F4] border border-[#F4C0D1] rounded-[12px] px-4 py-3">
            <p className="font-['DM_Sans'] text-[13px] text-[#993556]">{error}</p>
          </div>
        )}

        {/* Title */}
        <div className="flex flex-col gap-1">
          <label className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Our Paris trip"
            maxLength={200}
            className="w-full h-[48px] px-4 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] placeholder-[#C4A9B2] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150"
          />
        </div>

        {/* Date */}
        <div className="flex flex-col gap-1">
          <label className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide">
            Date
          </label>
          <input
            type="date"
            value={targetDate}
            min={todayStr()}
            onChange={(e) => setTargetDate(e.target.value)}
            className="w-full h-[48px] px-4 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150"
          />
        </div>

        {/* Hint */}
        <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] leading-relaxed">
          Set a date and watch the seconds tick down together.
        </p>
      </main>
    </div>
  )
}
